/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, useRef } from 'react';
import { Outlet } from 'react-router';
import type { Route } from './+types/_app';
import { usePageUIConfig, mainPaddingDataAttributes } from '@/lib/routes/page-ui-config';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { fetchCategory, fetchCategoriesByIds } from '@/lib/api/categories.server';
import { collectDeepFetchIds, planNavFetch } from '@/lib/navigation/fetch-plan';
import { hasUnparsableFeaturedLinks } from '@/lib/navigation/normalize';
import { toVisibleSubtree } from '@/lib/navigation/visibility';
import { getLogger } from '@/lib/logger.server';
import Header from '@/components/header';
import {
    readHeaderSurfaceSwitch,
    resolveHeaderSurface,
    type HeaderSurfaceSetting,
} from '@/components/header/header-surface';
import AuthoredFooter from '@/components/footer/authored-footer';
import { readFooterFallbackSwitch } from '@/components/footer/footer-fallback';
import ResponsiveNavigationMenu from '@/components/navigation-menu-mega';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { useAuth } from '@/providers/auth';
import { useWishlistSession } from '@/providers/wishlist';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import { SkipLink } from '@/components/skip-link';
import {
    fetchComponentWithComponentData,
    type ComponentWithComponentData,
} from '@/lib/page-designer/component-loader.server';
import type { BuilderContent } from '@builder.io/sdk-react';
import {
    FOOTER_PLACEMENT,
    GLOBAL_MODEL,
    HEADER_PLACEMENT,
    NEWSLETTER_PLACEMENT,
    placementQuery,
} from '@/lib/builder/placements';
import { resolveBuilderEntries, resolveBuilderEntry } from '@/lib/builder/resolve.server';
import type { TargetedBuilderContent } from '@/lib/builder/targeting';
import NewsletterBand from '@/components/footer/newsletter-band';
import { fetchNavContentEntries, type NavContentEntries } from '@/lib/builder/nav-content.server';
import {
    isNavContentPreviewPath,
    resolveNavContentPreview,
    withPreviewTile,
    type NavContentPreview,
} from '@/lib/builder/nav-content-preview.server';
import { getBuilderApiKey } from '@/lib/builder/api-key.server';
import { adaptHeaderEntry, type AuthoredHeader } from '@/lib/header/authored-header';

/** Params Builder's editor and Preview button append to the model's Preview URL. */
const BUILDER_EDITOR_PARAMS = ['builder.frameEditing', 'builder.preview'];

const isBuilderEditorRequest = (url: URL): boolean =>
    BUILDER_EDITOR_PARAMS.some((param) => url.searchParams.has(param));

type LoaderData = {
    /** Resolved, not deferred — the L1 row has to be in the SSR shell (see the loader's note). */
    root: ShopperProducts.schemas['Category'];
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    headerComponent: Promise<ComponentWithComponentData | null>;
    navContent: Promise<NavContentEntries>;
    /** `/preview/nav-content` only: the edited tile and the subnav it opens on; null elsewhere. */
    navPreview?: NavContentPreview | null;
    /** The Builder entry for the `footer` placement, or null when none is published (SCO-FOO-01). */
    footerEntry: Promise<BuilderContent | null>;
    /** Every `above-footer-newsletter` entry, untargeted — the band matches them itself (SCO-FOO-07). */
    newsletterEntries: Promise<TargetedBuilderContent[]>;
    /** The adapted `header` entry (SCO-HEA-01/04). Resolved, not deferred — it carries the theme. */
    authoredHeader: AuthoredHeader;
    builderApiKey: string;
    /** True when Builder's visual editor loaded this URL, so the footer stays attachable. */
    builderEditing: boolean;
    /**
     * The header layout `?header=` asked for, or null when the switch is absent or
     * carries an unrecognised value (SCO-494 / SCO-495).
     */
    headerSurfaceSwitch: HeaderSurfaceSetting | null;
};

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Those categories requests are divided into two
 * discrete request phases; the first to only fetch the root categories, and subsequent multiple calls to fetch the
 * identified sub categories to be displayed in the navigation menu. This behavior ensures that the initial data
 * doesn't get overwritten/removed on subsequent client-side navigations.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

export async function loader({ context, request }: Route.LoaderArgs): Promise<LoaderData> {
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth });

    // SCAPI caps `levels` at 2, so the four-level Featured subnav (SCO-NAV-03) is split over
    // two sequential requests: the root brings L1+L2, then one chunked getCategories on the
    // L2 ids brings L3+L4. Deepening the root request rather than adding a third round keeps
    // the round-trip count where it was before SCO-109.
    const { rootLevels, deeperLevels } = planNavFetch(maxDepth);

    // Filtered here rather than per component: it also keeps hidden L2 branches out of the
    // deep fetch below, so nothing is requested that the menu will not render.
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, rootLevels).then(toVisibleSubtree);

    // A merchandiser typo in c_featuredLinks empties the column with nothing else to show
    // for it, so say so here rather than letting it fail silently (SCO-NAV-03 A).
    void rootCategoryPromise
        .then((rootCategory) => {
            const unparsable = (rootCategory.categories ?? []).filter(hasUnparsableFeaturedLinks).map((c) => c.id);
            if (unparsable.length > 0) {
                logger.warn('AppLayout: c_featuredLinks could not be parsed, featured links will not render', {
                    categoryIds: unparsable,
                    expectedFormat: '[{"text": "...", "url": "..."}]',
                });
            }
        })
        .catch(() => {
            // fetchCategory already logs its own failures.
        });

    // Chained off the root promise so both stream in parallel with the rest of the loader.
    const subCategoriesPromise =
        deeperLevels > 0
            ? rootCategoryPromise.then(async (rootCategory: ShopperProducts.schemas['Category']) => {
                  const deeper = await fetchCategoriesByIds(
                      context,
                      collectDeepFetchIds(rootCategory.categories),
                      deeperLevels
                  );
                  return deeper.map(toVisibleSubtree);
              })
            : Promise.resolve([]);
    // Fetch header embedded component data (non-blocking, streamed to client, should be blocking once data is available from KVS to avoid layout shift)
    const headerComponentPromise = fetchComponentWithComponentData(
        { context, request, params: {} } as Route.LoaderArgs,
        { componentId: 'header' }
    );

    // Below the fold, so it is never awaited here — the footer streams in and degrades to the
    // code-owned one when Builder has nothing for this URL, is unreachable, or the ?footer=
    // fallback QA switch (SCO-498) asked for the fallback explicitly.
    // Key comes from BUILDER_PUBLIC_API_KEY on MRT.
    const builderApiKey = getBuilderApiKey();
    const requestUrl = new URL(request.url);
    // Gated off in Builder's visual editor: forcing the fallback there would remove the `editing`
    // mount an unpublished entry needs to attach to (see authored-footer.tsx).
    const builderEditing = isBuilderEditorRequest(requestUrl);
    const footerFallback = !builderEditing && readFooterFallbackSwitch(requestUrl.searchParams);
    if (footerFallback) {
        logger.debug('AppLayout: ?footer=fallback forced the code-owned footer; Builder fetch skipped');
    }
    const footerEntryPromise =
        builderApiKey && !footerFallback
            ? resolveBuilderEntry({
                  model: GLOBAL_MODEL,
                  apiKey: builderApiKey,
                  urlPath: requestUrl.pathname,
                  query: placementQuery(FOOTER_PLACEMENT),
              })
            : Promise.resolve(null);

    // Deliberately no `urlPath`: the band matches targeting client-side, because this loader runs
    // once per document load and a server-matched entry would freeze on the landing URL.
    const newsletterEntriesPromise = builderApiKey
        ? resolveBuilderEntries({
              model: GLOBAL_MODEL,
              apiKey: builderApiKey,
              query: placementQuery(NEWSLETTER_PLACEMENT),
          })
        : Promise.resolve([]);

    // One request for every subnav tile, never awaited: a Builder outage or a slow read cannot
    // hold up the header (SCO-NAV-03 D).
    const liveNavContentPromise = builderApiKey
        ? fetchNavContentEntries({ apiKey: builderApiKey, urlPath: requestUrl.pathname })
        : Promise.resolve({});

    // Builder's nav-content preview renders the real header with the edited tile held open.
    const navPreviewPromise =
        builderApiKey && isNavContentPreviewPath(requestUrl)
            ? rootCategoryPromise.then((rootCategory) =>
                  resolveNavContentPreview({ apiKey: builderApiKey, url: requestUrl, root: rootCategory })
              )
            : Promise.resolve(null);
    const navContentPromise = Promise.all([liveNavContentPromise, navPreviewPromise]).then(([tiles, preview]) =>
        withPreviewTile(tiles, preview)
    );
    // Both chain off the root fetch: if it fails the loader throws before returning them, and an
    // orphaned rejection kills the dev server (Node exits on unhandled rejections).
    subCategoriesPromise.catch(() => undefined);
    navContentPromise.catch(() => undefined);

    // Started here, awaited at the return, so it runs alongside the requests above rather than
    // after them. Awaited at all because deferring the theme or the logo means a visible repaint.
    const headerEntryPromise = builderApiKey
        ? resolveBuilderEntry({
              model: GLOBAL_MODEL,
              apiKey: builderApiKey,
              urlPath: requestUrl.pathname,
              query: placementQuery(HEADER_PLACEMENT),
              // Oldest wins, so a second `header` entry cannot hijack the live one.
              sort: { createdDate: 1 },
              // Read as fields, so no `<Content>` live editing — this is what shows a draft.
              searchParams: requestUrl.searchParams,
          })
        : Promise.resolve(null);

    return {
        // Awaited, not deferred: a deferred L1 row streams in last and then re-suspends on
        // hydration, so the header flashes navless and shifts the page by its height.
        root: await rootCategoryPromise,
        subs: subCategoriesPromise,
        headerComponent: headerComponentPromise,
        navContent: navContentPromise,
        navPreview: await navPreviewPromise,
        footerEntry: footerEntryPromise,
        newsletterEntries: newsletterEntriesPromise,
        authoredHeader: adaptHeaderEntry(await headerEntryPromise),
        builderApiKey,
        builderEditing,
        /*
         * QA switch: `?header=solid|transparent-light|transparent-dark` forces the header layout
         * for this document load, on the homepage's `?homepage=fallback` pattern. Read here rather
         * than in the component so the SSR'd HTML already carries the surface QA asked for. It is
         * still gated by the route opt-in, so it is inert off the homepage.
         */
        headerSurfaceSwitch: readHeaderSurfaceSwitch(requestUrl.searchParams),
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: {
        root,
        subs,
        headerComponent,
        navContent,
        footerEntry,
        newsletterEntries,
        // Defaulted: the layout is expected to survive partial loader data.
        authoredHeader = {},
        builderApiKey,
        builderEditing,
        headerSurfaceSwitch = null,
        navPreview = null,
    },
}: {
    loaderData: LoaderData;
}) {
    const refSubs = useRef<Promise<ShopperProducts.schemas['Category'][]> | undefined>(undefined);
    const refHeaderComponent = useRef<Promise<ComponentWithComponentData | null> | undefined>(undefined);
    const refNavContent = useRef<Promise<NavContentEntries> | undefined>(undefined);
    if (!refSubs.current && !refHeaderComponent.current && !refNavContent.current) {
        refSubs.current = subs;
        refHeaderComponent.current = headerComponent;
        refNavContent.current = navContent;
    }

    // Suspense tracks promises by identity, so the footer entry is pinned on first render too.
    const refFooterEntry = useRef<Promise<BuilderContent | null> | undefined>(undefined);
    const footerEntryPromise = (refFooterEntry.current ??= footerEntry);

    const refNewsletterEntries = useRef<Promise<TargetedBuilderContent[]> | undefined>(undefined);
    const newsletterEntriesPromise = (refNewsletterEntries.current ??= newsletterEntries);

    // Reflect the route's `handle.ui` config onto <main> as data-* attributes
    // during render, so the correct top padding is present in the SSR'd HTML.
    // If your CSS keys <main> padding off these, emitting them at render (not
    // in a post-hydration effect) avoids a layout shift (CLS) when the padding
    // would otherwise be added after first paint. Inert if no CSS matches.
    const pageUIConfig = usePageUIConfig();
    // `<main>` is told the *resolved* surface, not the route's opt-in, so hero
    // bleed and header theme cannot disagree (SCO-HEA-04).
    const appConfig = useConfig();
    // `?header=` wins for QA, then Builder; `pages.home.headerSurface` answers when nothing is
    // published. (FSD-HEA-04 says Business Manager, the TSD says Builder — this ships either way.)
    // The route opt-in still gates all three, including the QA switch.
    const headerSurface = resolveHeaderSurface(
        pageUIConfig,
        headerSurfaceSwitch ?? authoredHeader.surfaceSetting ?? appConfig.pages.home.headerSurface
    );
    const mainPaddingAttrs = mainPaddingDataAttributes(pageUIConfig, headerSurface !== 'solid');

    // Bind the module-level wishlist store (see providers/wishlist) to the current shopper here,
    // once, from client auth context. `customerId` only changes on login/logout — both are
    // redirects that revalidate the root loader, refreshing `useAuth()` — so the shell re-runs
    // this on every identity change and evicts the prior shopper's hearts. Plain browse never
    // changes it. The actual read stays lazy, fired by the first product intent.
    useWishlistSession(useAuth()?.customerId ?? null);

    // <WishlistMergeToast> stays at the app shell — it reads URL params and a one-time
    // cookie set by the post-login redirect target, not wishlist state.
    return (
        <>
            <SkipLink />
            <WishlistMergeToast />
            <Header
                surface={headerSurface}
                content={authoredHeader.content}
                announcementSlot={
                    <EmbeddedComponentRegion component={refHeaderComponent.current} regionId="announcement" />
                }>
                <ResponsiveNavigationMenu
                    resolve={root}
                    defer={refSubs.current}
                    navContent={refNavContent.current}
                    builderApiKey={builderApiKey}
                    preview={navPreview?.target ?? undefined}
                />
            </Header>
            <main id="main-content" tabIndex={-1} {...mainPaddingAttrs}>
                <Outlet />
            </main>
            {/* One `mt-auto` for both bands: `<footer>` carries its own, which would otherwise take
                the free space and leave the newsletter floating above it on a short page. */}
            <div className="mt-auto">
                {/* Not in the editor: the SDK routes `contentUpdate` by model name alone, so a
                    second `global` <Content> here would redraw itself every time someone edits the
                    footer. The band is authored on /preview/newsletter instead.
                    fallback={null}: the band is absent on most URLs, so reserving height for one
                    that usually does not render would cost more shift than it saves. */}
                {!builderEditing && (
                    <Suspense fallback={null}>
                        <NewsletterBand entries={newsletterEntriesPromise} apiKey={builderApiKey} />
                    </Suspense>
                )}
                {/* fallback={null}: the footer is below the fold, so a skeleton would cost more than it saves. */}
                <Suspense fallback={null}>
                    <AuthoredFooter entry={footerEntryPromise} apiKey={builderApiKey} editing={builderEditing} />
                </Suspense>
            </div>
        </>
    );
}
